export interface UserFieldValidationResult {
  isExternalUserIdValid: boolean
  isEmailValid: boolean
  isPhoneValid: boolean
  canSubmit?: boolean
}

export interface ValidateUserFieldsOptions {
  email: string
  phone: string
  externalUserId?: string
  name?: string
}

const EMAIL_USER_PATTERN =
  /^[-!#$%&'*+/=?^_`{}|~0-9A-Z]+(?:\.[-!#$%&'*+/=?^_`{}|~0-9A-Z]+)*$/i
const EMAIL_DOMAIN_LABEL_PATTERN = /^[A-Z0-9](?:[A-Z0-9-]{0,61}[A-Z0-9])?$/i

export function isValidEmail(value: string): boolean {
  const email = value.trim()
  if (!email || email.length > 320 || !email.includes("@")) return false

  const separatorIndex = email.lastIndexOf("@")
  const userPart = email.slice(0, separatorIndex)
  let domainPart = email.slice(separatorIndex + 1)

  if (!EMAIL_USER_PATTERN.test(userPart)) return false
  if (domainPart === "localhost") return true
  if (/[\s/:?#%\\]/.test(domainPart)) return false

  try {
    domainPart = new URL(`http://${domainPart}`).hostname
  } catch {
    return false
  }

  const labels = domainPart.split(".")
  const topLevelDomain = labels.at(-1) || ""
  return (
    labels.length >= 2 &&
    topLevelDomain.length >= 2 &&
    topLevelDomain.length <= 63 &&
    labels.every((label) => EMAIL_DOMAIN_LABEL_PATTERN.test(label))
  )
}

const LINE_USER_ID_PATTERN = /^U[0-9a-f]{32}$/

export function isValidClientUserIdentity(
  value: string,
  channelType: string
): boolean {
  const identity = value.trim()
  if (!identity) return false

  switch (channelType) {
    case "line":
      return LINE_USER_ID_PATTERN.test(identity)
    case "slack":
      return identity.includes("@")
        ? isValidEmail(identity)
        : !/\s/.test(identity)
    case "messenger":
    case "instagram":
      return !/\s/.test(identity)
    default:
      return true
  }
}

export function validateUserFields({
  email,
  phone,
  externalUserId,
  name,
}: ValidateUserFieldsOptions): UserFieldValidationResult {
  const externalUserIdTrimmed = externalUserId?.trim() || ""
  const emailTrimmed = email.trim()
  const phoneTrimmed = phone.trim()
  const nameTrimmed = name?.trim() || ""

  const phoneAllowed = /^\+?[0-9 ()-]{7,25}$/
  const phoneDigits = phoneTrimmed.replace(/\D/g, "")

  const isExternalUserIdValid =
    externalUserId === undefined || !!externalUserIdTrimmed
  const isEmailValid = !emailTrimmed || isValidEmail(emailTrimmed)
  const isPhoneValid =
    !phoneTrimmed ||
    (phoneAllowed.test(phoneTrimmed) &&
      phoneDigits.length >= 7 &&
      phoneDigits.length <= 15)

  const result: UserFieldValidationResult = {
    isExternalUserIdValid,
    isEmailValid,
    isPhoneValid,
  }

  if (name !== undefined) {
    // canSubmit is only computed by the create-user flow, which always
    // supplies externalUserId, so it is the required identity here.
    result.canSubmit =
      !!externalUserIdTrimmed && !!nameTrimmed && isEmailValid && isPhoneValid
  }

  return result
}
